import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type {
  PlaneSize,
  PlaneTypes,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { getMerchandiserCard } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/merchandiser/helpers/GetMerchandiserCard';
import { getMerchandiserPlaceholderGap } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/merchandiser/helpers/GetMerchandiserPlaceholderGap';
import { getWindowProps } from '@Utils/window';

import type {
  MerchandiserView,
  ProductImageDesign,
} from '../../common/MultiSectionView.types';
import { getProductImages } from './MerchandiserCommon';

const MERCHANDISER_WIDTH = 375;
const MERCHANDISER_HEIGHT = 667;
const MERCHANDISER_PORTRAIT_ASPECT_RATIO = 375 / 667;

const CONTENT_WIDTH = 344; // 329
const CONTENT_HEIGHT = 493;
const CONTENT_ASPECT_RATIO = CONTENT_WIDTH / CONTENT_HEIGHT;

const HEAD_GAP = 111; // Space for the 'Explore the collection' label to appear // check Figma

// These are derived from Figma
const productImageDesigns: ProductImageDesign[] = [
  {
    size: 142,
    left: 119,
    top: 390 - HEAD_GAP,
    animatedOutDx: -2,
    animatedOutDy: -19,
    radius: 45,
  },
  {
    size: 93,
    left: 264 - 28,
    top: 511 - HEAD_GAP,
    animatedOutDx: -1,
    animatedOutDy: -47,
    radius: 45,
  },
  {
    size: 104,
    left: 104 - 28,
    top: 0,
    animatedOutDx: -29,
    animatedOutDy: 48,
    radius: 45,
  },
  {
    size: 81,
    left: 245 - 28,
    top: 127 - HEAD_GAP,
    animatedOutDx: 11,
    animatedOutDy: 30,
    radius: 45,
  },
  {
    size: 59,
    left: 272 - 28,
    top: 418 - HEAD_GAP,
    animatedOutDx: 28,
    animatedOutDy: 54,
    radius: 45,
  },
];

export const layoutMerchandiserWithHeight = ({
  section,
  layoutTable,
  height,
}: {
  section: MerchandiserView;
  layoutTable: GridCard<PlaneTypes>[][];
  height: number;
}): GridCard<PlaneTypes.Layout_Merchandiser> => {
  let merchandiserCard: GridCard<PlaneTypes.Layout_Merchandiser> | undefined =
    undefined;

  layoutTable.forEach((column, index) => {
    if (index === 0) {
      const card = getMerchandiserCard(section, height);
      merchandiserCard = card;
      return column.push(card);
    }

    column.push(getMerchandiserPlaceholderGap(height));
  });

  return merchandiserCard!;
};

export const calculateMerchandiserInternalsPortrait = (
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>
) => {
  const { data } = gridCard;
  const boxSize = getBoxSizePortrait(gridCard);
  const contentBoxSize = getContentBoxSize(boxSize);
  const contentPosition = getContentPosition({ boxSize, contentBoxSize });
  const centralImageSize = getCentralImageSize(contentBoxSize);
  const centralImagePosition = getCentralImageSizePosition({
    contentBoxSize,
    centralImageSize,
  });
  const exploreLabelPosition = getExploreLabelPosition({
    boxSize,
    contentSize: contentBoxSize,
    contentPosition,
  });

  const productImages = getProductImages(gridCard, contentBoxSize, {
    productImageDesigns,
    contentHeightDesign: CONTENT_HEIGHT,
    contentWidthDesign: CONTENT_WIDTH,
  });

  data!.internals = {
    boxSize,
    contentBoxSize,
    contentPosition,
    centralImageSize,
    centralImagePosition,
    productImages,
    exploreLabel: {
      position: exploreLabelPosition,
      anchorY: 'bottom',
      fontSize: 26,
      color: data?.dtoData.fontColour,
      text: `Explore the\ncollection`,
      lineHeight: 30 / 26,
      animateOutPosition: {
        x: exploreLabelPosition.x,
        y: exploreLabelPosition.y - 30,
      },
    },
  };
};

export const getHeightPortrait = (): number => {
  const { width, height } = getWindowProps();
  const aspectHeight = Math.floor(width / MERCHANDISER_PORTRAIT_ASPECT_RATIO);

  return Math.min(aspectHeight, height); // makes sure that height does not exceed screen height
};

const getBoxSizePortrait = (
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>
): PlaneSize => ({
  width: getWindowProps().width,
  height: gridCard.size.height,
});

const getContentBoxSizeBasedOnWidth = (boxSize: PlaneSize): PlaneSize => {
  const width = (CONTENT_WIDTH / MERCHANDISER_WIDTH) * boxSize.width;
  // There is a small 'width' window range that height gets outside the box
  // We confine it within the box this way
  const height = Math.min(
    width / CONTENT_ASPECT_RATIO,
    boxSize.height - HEAD_GAP - 10
  );

  return { width, height };
};

const getContentBoxSizeBasedOnHeight = (boxSize: PlaneSize): PlaneSize => {
  const height = (CONTENT_HEIGHT / MERCHANDISER_HEIGHT) * boxSize.height;
  const width = height * CONTENT_ASPECT_RATIO;

  return { width, height };
};

const getContentBoxSize = (boxSize: PlaneSize): PlaneSize => {
  const boxAspectRatio = boxSize.width / boxSize.height;

  return boxAspectRatio <= CONTENT_ASPECT_RATIO
    ? getContentBoxSizeBasedOnWidth(boxSize)
    : getContentBoxSizeBasedOnHeight(boxSize);
};

const getContentPosition = ({
  boxSize,
  contentBoxSize,
}: {
  boxSize: PlaneSize;
  contentBoxSize: PlaneSize;
}): Position2D => ({
  x: 0,
  y: Math.round(boxSize.height / 2 - HEAD_GAP - contentBoxSize.height / 2),
});

const getCentralImageSize = (contentBoxSize: PlaneSize): PlaneSize => ({
  width: Math.floor((320 / CONTENT_WIDTH) * contentBoxSize.width),
  height: Math.floor((263 / CONTENT_HEIGHT) * contentBoxSize.height),
});

const getCentralImageSizePosition = ({
  contentBoxSize,
  centralImageSize,
}: {
  contentBoxSize: PlaneSize;
  centralImageSize: PlaneSize;
}): Position2D => {
  const topInDesign = 165 - 111;
  const top = Math.floor(
    (topInDesign / CONTENT_HEIGHT) * contentBoxSize.height
  );

  const y = -Math.round(
    centralImageSize.height / 2 + top - contentBoxSize.height / 2
  );

  return {
    x: 0,
    y,
  };
};

const getExploreLabelPosition = ({
  contentSize,
}: {
  boxSize: PlaneSize;
  contentSize: PlaneSize;
  contentPosition: Position2D;
}) => ({
  x: -contentSize.width / 2,
  // the Explore label is out of the content Box but positioned
  // relative to its center and is anchored at 'Bottom' of the text
  y: contentSize.height / 2 + (HEAD_GAP - 30 * 2 - 15),
});
